import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, FolderOpen, CheckCircle, X, Loader2, AlertCircle, FileText } from 'lucide-react';
import { BiddingItemUploadStepCard } from '../../../components/admin/BiddingItemUpload/BiddingItemUploadStepCard';
import { useExcelQuery, type convertExcelResult } from '../../../hooks/useConvertExcel';
import type { ExcelInfoItem } from '../../../types';
import { useBulkCreateBiddingItemMutation } from '../../../hooks/useBiddingItemsQuery';
import toast from 'react-hot-toast';
import { resultExcelFileName, templateExcelFileName } from '../../../constants/ExcelItemsConst';
import { useGetBiddingDateByPrefectureForApplyQuery } from '../../../hooks/useCalendarQuery';
import { CombinedCategoryOptions, ListingCategoryConst, parseCombinedCategory, PrefTypeConst } from '../../../constants';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '../../../types/UserRole';
import { useMasters } from '../../../contexts/MastersContext';
import { useFarmerPdfUrlQuery } from '../../../hooks/useMasterReportQuery';
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

type resultCount = { successCount: number, failureCount: number};

/**
 * 出品情報一括登録
 * @returns 
 */
const BiddingItemUploadPage = () => {

    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    //Hooks
    const bulkCreateItemMutation = useBulkCreateBiddingItemMutation();  // 出品アイテム登録
    const {DownloadExcel, DownloadResultExcel, ConvertExcel} = useExcelQuery();
    const { refetch: refetchFarmerPdf, isFetching: isFetchingFarmerPdf } = useFarmerPdfUrlQuery({ enabled: false });
    const { currentUser } = useAuth();  // 認証Context
    const { isMastersLoading, sellers } = useMasters();

    //State
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [_isCreatingNewItem, setIsCreatingNewItem] = useState(false);  // 新規登録モードの状態管理(true:新規登録モード中)
    const [apiResults, setApiResults] = useState<resultCount | null>({successCount:0, failureCount:0});
    const [isPrinting, setIsPrinting] = useState(false);

    const isJA = currentUser?.role === UserRole.JA;         // JA判定
    const isSeller = currentUser?.role === UserRole.Seller; // 売手判定
    const isFarmer = currentUser?.role === UserRole.Farmer; // 生産者判定

    // ファイル選択ボタンが押された時の処理
    const handleFileSelectClick = () => {
        fileInputRef.current?.click();
    };

    // ファイル削除処理
    const onClear = () => {
        setSelectedFile(null);
    };

    // ファイルが選択された時の処理
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        //TODO:再選択処理などは未検証
        setSelectedFile(file);
        // 同じファイルを再度選べるように、inputの値をリセットする
        event.target.value = "";
    };

    // 検索条件
    interface bulkUploadFilter {
        biddingDate?: number,
        listingCategory?: number,
        prefectureType?: number
    }
    const [activeFilters, setActiveFilters] = useState<bulkUploadFilter>({
        biddingDate: 0,
        listingCategory: ListingCategoryConst.BIDDING,
        prefectureType: PrefTypeConst.INTERNAL,
    });
    
    // 初期化が完了したかどうかを判定するフラグ
    const [isFilterInitialized, setIsFilterInitialized] = useState(false);

    // 日付取得フック (activeFilters?.prefectureTypeが変更されると自動で再取得が走る)
    const { data: initialBiddingDate, isLoading: isDateLoading, isFetching } = useGetBiddingDateByPrefectureForApplyQuery(activeFilters.prefectureType ?? 0, currentUser?.loginId);

    // 日付を表示形式(yyyy年MM月dd日)に変換
    const formatYMD = (dateStrOrNum: string | number | undefined | null, format: 'ja' | 'slash' | 'hyphen' = 'ja'): string => {
        if (!dateStrOrNum || dateStrOrNum === 0) return '未定'; // 0または空の場合は未定などを表示
        
        const str = String(dateStrOrNum);
        if (!/^\d{8}$/.test(str)) {
            console.warn(`無効な日付形式です: ${str}。8桁の数字を指定してください。`);
            return '';
        }
        const year = str.substring(0, 4);
        const month = str.substring(4, 6);
        const day = str.substring(6, 8);

        switch (format) {
            case 'slash': return `${year}/${month}/${day}`;
            case 'hyphen': return `${year}-${month}-${day}`;
            case 'ja':
            default: return `${year}年${month}月${day}日`;
        }
    }

    // テンプレートファイルのダウンロード
    const handleDownload = async () => {
        try {
            await DownloadExcel(templateExcelFileName);
        } catch(ex) {
            alert("ひな形ダウンロードに失敗しました。");
        }
    };

    // アップロード処理
    const handleUpload = async () =>{
        setIsLoading(true);
        try {
            if(selectedFile) {
                const result: convertExcelResult = await ConvertExcel(selectedFile, activeFilters.biddingDate);
                if(result.result && (result.data !== null) && ((result.data as ExcelInfoItem[]).length !== 0)) {
                    await handleSaveNewItem(result.data, Number(activeFilters.prefectureType));
                } else {
                    toast.error(result.message, {duration:5000});
                }
            } else {
                alert("まずはファイルを選択してください");
                return;
            }
        } catch (error) {
            console.error("Excel読み込みエラー:", error);
            // alert("ファイルの読み込みに失敗しました");
        } finally {
            setIsLoading(false);
        }
    }

    // 出品アイテム新規登録実行
    const handleSaveNewItem = useCallback(async (newItem: ExcelInfoItem[], prefectureType: number) => {
        await bulkCreateItemMutation.mutateAsync(
        { newItemDataList: newItem , prefectureType: prefectureType}, 
        {
            async onSuccess(data) {
                setApiResults(prev =>  { 
                    if(!prev) return prev;
                    return {...prev, successCount: data?.successCount || 0, failureCount: data?.failureCount || 0};
                });
                if(data !== null && data.excelData !== null && data.excelData?.length !== 0) {
                    await DownloadResultExcel(data.excelData || '', `${resultExcelFileName}_${initialBiddingDate}`);
                } else {
                    toast("登録結果Excelのダウンロードに失敗しました。");
                }
                setIsCreatingNewItem(false);
            },
            onError(error) {
                console.error('新規登録エラー:',error);
            },
            onSettled(error) {
                if (error) console.error('新規登録エラー:',error);
            },
        });
    }, [initialBiddingDate, bulkCreateItemMutation, DownloadResultExcel]);

    // セレクトボックス変更時処理
    const handleCombinedCategoryChange = useCallback((selectedValue: string) => {
        const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);
        
        setActiveFilters(prev => ({
          ...prev,
          listingCategory: listingCategory as number,
          prefectureType: prefCategory as number, 
        }));
    }, []);

    // 生産者一覧を表示処理
    const handlePrintFarmerList = useCallback(async () => {
        if (isFetchingFarmerPdf || isPrinting) return;
        
        try {
            setIsPrinting(true);
            
            const { data: pdfUrl, isError } = await refetchFarmerPdf();
            if (isError || !pdfUrl) {
                toast.error("生産者一覧PDFの取得に失敗しました。");
                setIsPrinting(false);
                return;
            }

            // 既存の印刷用iframeがあれば削除
            const oldIframe = document.getElementById('pdf-print-iframe');
            if (oldIframe) document.body.removeChild(oldIframe);

            // 見えないiframeを作成してPDFを読み込ませる
            const iframe = document.createElement('iframe');
            iframe.id = 'pdf-print-iframe';
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            iframe.src = pdfUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                // PDFの読み込み完了後、少し待ってから印刷ダイアログを起動
                setTimeout(() => {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                    
                    // ダイアログが立ち上がったタイミングで「準備中...」を解除
                    setIsPrinting(false);
                    URL.revokeObjectURL(pdfUrl);
                }, 500);
            };
        } catch (err) {
            console.error(err);
            toast.error("プレビュー中にエラーが発生しました。");
            setIsPrinting(false);
        }
    }, [refetchFarmerPdf, isFetchingFarmerPdf, isPrinting]);

    // 一括更新用のハンドラ
    const handleRefreshAll = async () => {
        // ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
        queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
    };

    // 初期化処理：マスタデータやユーザー情報が取得完了したタイミングで1度だけ実行
    useEffect(() => {
        // 【修正】isFilterInitialized フラグを使って1回だけ実行されるように担保
        if (isFilterInitialized) return;
        // マスタデータローディング中、またはユーザー情報がない場合は待機
        if (isMastersLoading || !currentUser) return;

        let defaultPref: number = PrefTypeConst.INTERNAL;
        
        const targetSellerCode = currentUser?.parentCode;
        if ((isSeller || isFarmer) && targetSellerCode) {
            const myData = sellers.find(s => String(s.sellerCode) === String(targetSellerCode));
            if (myData) {
                defaultPref = myData.prefectureType;
            }
        }
        
        // 所属に応じた初期区分をセットし、初期化完了フラグを立てる
        setActiveFilters(prev => ({
            ...prev,
            prefectureType: defaultPref,
        }));
        setIsFilterInitialized(true);

    }, [isMastersLoading, currentUser, isSeller, isFarmer, sellers, isFilterInitialized]);

    // フックから取得した入札日（initialBiddingDate）を検索条件（activeFilters）に同期する
    useEffect(() => {
        if (initialBiddingDate) {
            setActiveFilters(prev => {
                const newDateNum = Number(initialBiddingDate);
                // 変更がない場合は更新しない（無限ループ防止）
                if (prev.biddingDate === newDateNum) return prev;
                return {
                    ...prev,
                    biddingDate: newDateNum
                };
            });
        }
    }, [initialBiddingDate]);

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-4 bg-gray-50">
                <div className="max-w-2xl mx-auto space-y-4">

                    {/* テンプレートファイルのダウンロード */}
                    <BiddingItemUploadStepCard stepNumber={1} title="出品情報登録用Excelの準備" color="blue">
                        <p className="text-slate-500 mb-3 max-w-lg">
                            出品情報登録用Excelをダウンロードし、商品情報を入力してください。<br/>
                            <span className="text-xs text-slate-400">※必須項目は必ず入力してください</span>
                        </p>
                        <button 
                            onClick={handleDownload}
                            className="w-full max-w-md py-4 rounded-xl flex items-center justify-center gap-2 font-bold shadow-lg transition-all bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200 hover:scale-[1.02]"
                        >
                        <FileSpreadsheet size={20} />
                            出品情報登録用Excelをダウンロード
                        </button>
                        
                        {/* Seller または JA の時だけ表示する生産者一覧プレビューボタン */}
                        {(isSeller || isJA) && (
                            <button 
                                onClick={handlePrintFarmerList}
                                disabled={isFetchingFarmerPdf || isPrinting}
                                className={`w-full max-w-md mt-4 py-3 rounded-xl flex items-center justify-center gap-2 font-bold transition-all border-2 
                                    ${(isFetchingFarmerPdf || isPrinting)
                                        ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-wait' 
                                        : 'bg-white border-blue-600 text-blue-600 hover:bg-blue-50 hover:scale-[1.02] shadow-sm'
                                    }`}
                            >
                                {(isFetchingFarmerPdf || isPrinting) ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        準備中...
                                    </>
                                ) : (
                                    <>
                                        <FileText size={20} />
                                        {isJA ? '売手・生産者一覧を表示' : '生産者一覧を表示'}
                                    </>
                                )}
                            </button>
                        )}
                    </BiddingItemUploadStepCard>

                    {/* ファイルを選択 */}
                    <BiddingItemUploadStepCard stepNumber={2} title="ファイルを選択" color="slate">
                        <p className="text-slate-500 mb-3 max-w-lg">
                            データの入力が完了したExcelファイルを選択してください。
                        </p>
                        <div className="w-full max-w-md bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-3 hover:border-slate-400 transition-colors group">
                            <input 
                                type="file" 
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept=".xlsx,.xls"
                                className="hidden" 
                            />
                            <button 
                                onClick={handleFileSelectClick}
                                className="mx-auto px-8 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg flex items-center gap-2 font-medium transition-colors shadow-sm mb-4 group-hover:shadow-md"
                            >
                                <FolderOpen size={20} />
                                ファイルを選択
                            </button>
                            <div className="h-10 flex items-center justify-center">
                                {selectedFile ? (
                                    <div className="flex items-center gap-3 pl-4 pr-1.5 py-1.5 bg-emerald-50 text-emerald-800 rounded-full border border-emerald-200 shadow-sm animate-fade-in">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle size={16} className="text-emerald-600" />
                                        <span className="font-bold text-sm">{selectedFile.name}</span>
                                    </div>
                                    <div className="w-px h-4 bg-emerald-200"></div>
                                    <button 
                                        onClick={onClear}
                                        className="p-1.5 bg-white text-slate-400 border border-slate-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200 rounded-full transition-all shadow-sm group"
                                        title="選択を解除"
                                    >
                                        <X size={14} strokeWidth={2.5} className="group-hover:scale-110 transition-transform" />
                                    </button>
                                    </div>
                                ) : (
                                    <span className="text-slate-400 text-sm">選択されていません</span>
                                )}
                            </div>
                        </div>
                    </BiddingItemUploadStepCard>

                    {/* アップロードして登録を実行 */}
                    <BiddingItemUploadStepCard stepNumber={3} title="アップロードして登録を実行" color="emerald">
                        <Upload className="absolute -right-10 -bottom-10 text-emerald-50 opacity-40 transform rotate-12" size={200} />
                        <div className="relative z-10 w-full flex flex-col items-center">
                            <div className="flex flex-wrap items-end">
                            {/* 1. 出品区分（セレクトボックス） */}
                            <div className="w-full sm:w-auto mr-6">
                                {/* <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1 text-left">
                                    出品区分（県内/県外）
                                </label> */}
                                <select
                                    disabled={isSeller || isFarmer || isJA}
                                    value={
                                        (activeFilters?.listingCategory != null && activeFilters?.prefectureType != null)
                                            ? `${activeFilters.listingCategory}_${activeFilters.prefectureType}`
                                            : ""
                                    }
                                    onChange={(e) => handleCombinedCategoryChange(e.target.value)}
                                    className={`w-full sm:w-56 p-2 border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                        (isSeller || isFarmer || isJA) ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white hover:bg-slate-50"
                                    }`}
                                >
                                    {CombinedCategoryOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 2. 次回の入札日（テキスト表示部分） */}
                            <div className="flex items-baseline pb-2">
                                <p className="text-xl text-slate-700 mr-2">
                                    次回の入札日：
                                </p>
                                <p className="text-2xl text-slate-700 font-bold min-w-[140px]">
                                    {isDateLoading ? (
                                        <span className="text-sm text-slate-500 flex items-center gap-1 font-normal">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            取得中...
                                        </span>
                                    ) : (
                                        <>{formatYMD(activeFilters.biddingDate)}</>
                                    )}
                                </p>
                            </div>
                        </div>
                            <button 
                                onClick={handleUpload}
                                className={`
                                    w-full max-w-md py-5 mt-4 rounded-xl flex items-center justify-center gap-3 font-bold text-xl shadow-lg transition-all
                                    ${isLoading 
                                        ? 'bg-emerald-50 text-emerald-400 border-2 border-emerald-100 cursor-wait shadow-none' 
                                        : selectedFile 
                                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-200 hover:scale-[1.02]' 
                                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                    }
                                `}
                                disabled={isLoading || !selectedFile}
                            >
                                {isLoading? (  
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>アップロード中...</span>
                                    </>
                                ) : (  
                                    <>
                                        <Upload size={28} />
                                        3.アップロードして登録を実行
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="h-14 w-md mt-3 -mb-2 flex items-center text-xl justify-center gap-4 bg-transparent rounded-lg animate-in fade-in slide-in-from-top-1">
                            <div className="flex items-center gap-1.5 text-emerald-600 font-bold">
                            <CheckCircle className="w-4 h-4" />
                            <span>成功: {apiResults?.successCount}件</span>
                            </div>
                            <div className="w-px h-4 bg-slate-300"></div>
                            <div className={`flex items-center gap-1.5 font-bold ${(apiResults?.failureCount || 0) > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                            <AlertCircle className="w-4 h-4" />
                            <span>失敗: {apiResults?.failureCount}件</span>
                            </div>
                        </div>
                    </BiddingItemUploadStepCard>
                </div>
            </main>
            <button
                onClick={handleRefreshAll}
                disabled={isFetching}
                className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-30 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
                title="最新の情報に更新"
            >
                <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
        </div>
    );
};

export default BiddingItemUploadPage;